import React, { useState, useEffect } from 'react';

const CrisisAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      // Mock data based on CLEAR system specification
      const mockAlerts = [
        {
          event_id: 'EID-2024-001',
          detection_time: '2024-03-15 14:30',
          status: 'Active',
          confidence: 85,
          severity: 70,
          crisis_type: 'Flood',
          location: 'Country A, Region X, Lat: 12.34, Lon: 56.78, Radius: 5km'
        },
        {
          event_id: 'EID-2024-002',
          detection_time: '2024-03-16 09:15',
          status: 'Resolved',
          confidence: 92,
          severity: 88,
          crisis_type: 'Earthquake',
          location: 'Country B, Region Y, Lat: 34.56, Lon: 78.90, Radius: 2km'
        },
        {
          event_id: 'EID-2024-003',
          detection_time: '2024-03-17 18:45',
          status: 'Active',
          confidence: 78,
          severity: 65,
          crisis_type: 'Conflict',
          location: 'Country C, Region Z, Lat: 56.78, Lon: 90.12, Radius: 10km'
        },
        {
          event_id: 'EID-2024-004',
          detection_time: '2024-03-18 12:00',
          status: 'Pending',
          confidence: 60,
          severity: 50,
          crisis_type: 'Drought',
          location: 'Country D, Region W, Lat: 78.90, Lon: 12.34, Radius: 20km'
        },
        {
          event_id: 'EID-2024-005',
          detection_time: '2024-03-19 07:30',
          status: 'Active',
          confidence: 88,
          severity: 82,
          crisis_type: 'Disease Outbreak',
          location: 'Country E, Region V, Lat: 90.12, Lon: 34.56, Radius: 1km'
        }
      ];
      
      setAlerts(mockAlerts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return '#C85A54'; // coral-500
      case 'resolved':
        return '#84A98C'; // sage-500 
      case 'pending':
        return '#C4975A'; // amber-500
      default:
        return '#64748B'; // slate-500
    }
  };

  const getConfidenceBar = (confidence) => {
    const color = confidence >= 80 ? '#84A98C' : confidence >= 60 ? '#C4975A' : '#C85A54';
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-2 bg-stone-200 overflow-hidden">
          <div 
            className="h-full transition-all duration-300" 
            style={{ width: `${confidence}%`, backgroundColor: color }}
          ></div>
        </div>
        <span className="text-xs font-mono">{confidence}%</span>
      </div>
    );
  };

  const getSeverityBar = (severity) => {
    const color = severity >= 80 ? '#C85A54' : severity >= 60 ? '#C4975A' : '#84A98C';
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-2 bg-stone-200 overflow-hidden">
          <div 
            className="h-full transition-all duration-300" 
            style={{ width: `${severity}%`, backgroundColor: color }}
          ></div>
        </div>
        <span className="text-xs font-mono">{severity}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
          <p className="mt-4 opacity-60">Loading crisis alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-light mb-4">Crisis Detection Engine Alerts</h1>
        <p className="text-base opacity-80 leading-relaxed">
          Monitor and manage alerts from the Crisis Detection Engine. View key information from the data schema, 
          including event ID, detection time, status, confidence score, severity score, crisis type, location details, 
          population impact, data sources, and indicators.
        </p>
      </div>

      <div className="card-minimal overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-stone-200">
              <tr>
                <th className="text-left py-4 px-4 text-sm font-mono">Event ID</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Detection Time</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Status</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Confidence</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Severity</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Crisis Type</th>
                <th className="text-left py-4 px-4 text-sm font-mono">Location</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.event_id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                  <td className="py-4 px-4 text-sm font-mono">{alert.event_id}</td>
                  <td className="py-4 px-4 text-sm">{alert.detection_time}</td>
                  <td className="py-4 px-4">
                    <span 
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-full"
                      style={{ backgroundColor: getStatusColor(alert.status) }}
                    >
                      {alert.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {getConfidenceBar(alert.confidence)}
                  </td>
                  <td className="py-4 px-4">
                    {getSeverityBar(alert.severity)}
                  </td>
                  <td className="py-4 px-4 text-sm">{alert.crisis_type}</td>
                  <td className="py-4 px-4 text-sm opacity-80">{alert.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrisisAlerts;